import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { QRCodeSVG } from 'qrcode.react';
import md5 from 'crypto-js/md5';

import { searchComponentInstances } from './searchComponentInstances';

import localUtil from '../../lib/util';
import { appAlert, appConfirm } from '../../lib/dialogs';
import dialogStore from '../../stores/dialog';
import { beginDialogAction } from '../../lib/dialogAction';

// DyreID's GetPetsDetails fallback returns the owner as one name string. The backend splits it
// (get-getchipinfo.js), but keep a local fallback so an older backend can never make us create
// an owner without a name. "Hagen, Marius" is last-name-first, otherwise the last word is the surname.
function splitOwnerName(fullName) {
    const name = `${fullName || ''}`.trim().replace(/\s+/g, ' ');
    if (!name) {
        return { firstname: '', lastname: '' };
    }
    if (name.includes(',')) {
        const [last, ...rest] = name.split(',');
        return { firstname: rest.join(',').trim(), lastname: last.trim() };
    }
    const parts = name.split(' ');
    if (parts.length === 1) {
        return { firstname: parts[0], lastname: '' };
    }
    return { firstname: parts.slice(0, -1).join(' '), lastname: parts[parts.length - 1] };
}

function visitorName(visitor) {
    return `${visitor?.firstname || ''} ${visitor?.lastname || ''}`.trim();
}

@withText(props => ({
    searchPlaceholderWithKey: <Text id='list.search-placeholder-with-key'>{'{title}'} (press {'{key}'})</Text>,
    dyreIdNotFound: <Text id='dyreid.not-found'>The animal was not found in DyreID</Text>,
    dyreIdGenericError: <Text id='dyreid.generic-error'>Error from DyreID</Text>,
    dyreIdNoChipMatch: <Text id='dyreid.no-chip-match'>No animal found in DyreID with chip number</Text>,
    dyreIdImportQuestion: <Text id='dyreid.import-question'>Import this animal from DyreID?</Text>,
    dyreIdCreateVisitor: <Text id='dyreid.create-visitor'>Create new owner</Text>,
    dyreIdUseVisitor: <Text id='dyreid.use-visitor'>Use existing owner</Text>,
    dyreIdCreateAnimal: <Text id='dyreid.create-animal'>Create new animal</Text>,
    dyreIdUseAnimal: <Text id='dyreid.use-animal'>Use existing animal</Text>,
    dyreIdMissingOwnerName: <Text id='dyreid.missing-owner-name'>DyreID has no owner name for this animal. Create the owner yourself, then add the animal.</Text>,
    dyreIdVisitorFailed: <Text id='dyreid.visitor-failed'>Could not create the owner. Nothing was imported.</Text>,
    dyreIdAnimalFailed: <Text id='dyreid.animal-failed'>Could not create the animal. Nothing was imported.</Text>,
}))
@observer
class DyrejournalSaleSearch extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            search: '',
            selectedIndex: 0,
            searching: false,
            focused: false,
            dyreIdInfo: null,
            dyreIdError: null,
            activeFilters: {},
        };
        this.displayResults = [];
        this.searchFieldRef = null;
        this.searchInputField = null;
        this.mainContainer = null;
        this.searchTimer = null;
        this.linesContainer = null;
        this.lines = {};
    }

    searchInput = (e) => {
        // Strip commas and collapse multiple spaces (users often paste "Normann, Ola")
        const search = e.target.value.replace(/,/g, ' ').replace(/\s+/g, ' ');
        const { holdingStore } = this.props;
        this.setState({ search, activeFilters: {} });
        this.setFocus();

        clearTimeout(this.searchTimer);
        if (search.length >= 3) {
            this.searchTimer = setTimeout(async () => {
                this.doSearch(search);
            }, 300);
        }
    }

    saveSearchLocalStorage = (search) => {
        if (search.length <= 3) {
            return;
        }
        const searchLower = search.toLowerCase();

        const { searchResultList, keyName = '' } = this.props;
        const searchHistory = util.get(`searchHistory-${searchResultList}-${keyName}`) || [];
        // Add to search history

        const updatedHistory = searchHistory.filter(term => {
            const termLower = term.toLowerCase();
            if (termLower === searchLower) {
                // Exact match, remove the old entry to re-add it at the top
                return false;
            }
            if (searchLower.startsWith(termLower)) {
                // Existing term is a prefix of the new term, remove it
                return false;
            }
            if (termLower.startsWith(searchLower)) {
                // New term is a prefix of an existing term, do not add new term
                return false;
            }
            return true; // Keep the term
        });

        // Add the new term to the top of the history
        updatedHistory.unshift(searchLower);

        // Limit list to 10 items
        if (updatedHistory.length > 5) {
            // Remove all other than the first 5 items
            updatedHistory.splice(5);
            // updatedHistory.shift();
        }
        util.set(`searchHistory-${searchResultList}-${keyName}`, updatedHistory);
    }

    async searchFromHistory(search) {
        this.setState({ search });
        this.doSearch(search);
        this.searchInputField.focus();
    }

    async doSearch(search) {
        const { holdingStore, searchResultList, query: inputQuery = {}, addData = [], filterResults } = this.props;
        const { saleStore, visitorStore, animalStore, productStore, appState } = this.props.stores;

        if (search) {
            // if (search.length >= 3) {
            //     this.saveSearchLocalStorage(search);
            // }
            // console.log('doUpdateField.saveField', id, field, value);
            this.setState({ searching: true });
            let result = await holdingStore.load({ query: { ...inputQuery, search }, addData, skipUpdate: true });

            // Apply client-side filtering if filterResults prop is provided
            if (filterResults && typeof filterResults === 'function') {
                result = filterResults(result, search);
            }

            if (searchResultList === 'visitorResults') {
                saleStore.updateKeyValue(searchResultList, result);
            } else if (searchResultList === 'animalResults') {
                saleStore.updateKeyValue(searchResultList, result);

                // Check if search looks like a chipID (15 digits) and no results found.
                // skipDyreIdImport disables the DyreID lookup/import path for callers
                // outside the sale flow (importFromDyreId writes into newSale).
                const isChipId = /^\d{15}$/.test(search.trim());
                if (!this.props.skipDyreIdImport && isChipId && (!result || result.length === 0)) {
                    console.log('ChipID detected, checking DyreID:', search);
                    // No local results, try DyreID lookup
                    const dyreIdResponse = await animalStore.dyreIdGetChipInfo(search.trim());

                    if (dyreIdResponse?.status === 200 && dyreIdResponse?.data) {
                        const dyreIdData = dyreIdResponse.data;

                        // Check if DyreID returned an error or invalid data
                        if (dyreIdResponse.dyreIdErrors && dyreIdResponse.dyreIdErrors.length > 0) {
                            this.setState({
                                dyreIdInfo: null,
                                dyreIdError: dyreIdResponse.dyreIdErrorSummary || this.props.dyreIdNotFound,
                            });
                            console.log('DyreID error:', dyreIdResponse.dyreIdErrorSummary);
                        } else if (dyreIdData.IsSuccess === 'false' || dyreIdData.ErrorMessage) {
                            // DyreID returned an error in the data
                            this.setState({
                                dyreIdInfo: null,
                                dyreIdError: dyreIdData.ErrorMessage || this.props.dyreIdGenericError,
                            });
                            console.log('DyreID data error:', dyreIdData.ErrorMessage);
                        } else {
                            // Valid data - show import option
                            await this.checkAndPrepareImport(dyreIdData);
                        }
                    } else {
                        this.setState({
                            dyreIdInfo: null,
                            dyreIdError: `${this.props.dyreIdNoChipMatch}: ${search}`,
                        });
                    }
                } else {
                    // Clear DyreID info if search is not a chipID or results were found
                    this.setState({
                        dyreIdInfo: null,
                        dyreIdError: null,
                    });
                }
            } else if (searchResultList === 'productResults') {
                saleStore.updateKeyValue(searchResultList, result);
            } else if (searchResultList === 'searchResults') {
                holdingStore.updateKeyValue(searchResultList, result);
            }
            this.setState({ searching: false });
        }
    }

    checkAndPrepareImport = async (dyreIdData) => {
        const { visitorStore, animalStore, saleStore } = this.props.stores;
        const { newSale } = saleStore;
        const { visitors = [] } = newSale;
        const selectedVisitor = visitors[0];

        // Extract phone number - remove whitespaces but keep leading + and digits
        let phoneNumber = dyreIdData.PrimaryTelephoneNo || dyreIdData.OwnerCellPhone;
        if (phoneNumber) {
            phoneNumber = phoneNumber.trim();
            // Remove all whitespaces
            phoneNumber = phoneNumber.replace(/\s/g, '');
            // Keep only + at the beginning and digits, remove everything else
            phoneNumber = phoneNumber.replace(/^(\+)?(.*)$/, (match, plus, rest) => {
                return (plus || '') + rest.replace(/\D/g, '');
            });
        }

        // Owner name: GetChipInfo returns it split, the GetPetsDetails fallback only as OwnerFullName.
        const ownerFullName = `${dyreIdData.OwnerFullName || ''}`.trim();
        const splitName = splitOwnerName(ownerFullName);
        const ownerFirstname = `${dyreIdData.OwnerFirstName || ''}`.trim() || splitName.firstname;
        const ownerLastname = `${dyreIdData.OwnerLastName || ''}`.trim() || splitName.lastname;
        const ownerName = `${ownerFirstname} ${ownerLastname}`.trim() || ownerFullName;

        // Check if visitor exists by phone
        let existingVisitor = null;
        if (phoneNumber) {
            // Normalize phone for search - remove +47 prefix to match how tokens are stored
            const searchPhone = phoneNumber.replace(/^\+47/, '');
            const visitorsResponse = await visitorStore.load({
                query: { search: searchPhone },
                skipUpdate: true,
            });
            if (visitorsResponse && visitorsResponse.length > 0) {
                existingVisitor = visitorsResponse[0];
                console.log('Visitor found:', existingVisitor);
            }
        } else if (ownerName) {
            // The GetPetsDetails fallback carries no phone number. Without a name lookup the import
            // would create a second copy of an owner the clinic already has. Only an unambiguous
            // match counts - exactly one visitor with that full name - so a name two clients share
            // never attaches the animal to the wrong person.
            const visitorsResponse = await visitorStore.load({
                query: { search: ownerName },
                skipUpdate: true,
            });
            const nameMatches = (visitorsResponse || [])
                .filter(v => visitorName(v).toLowerCase() === ownerName.toLowerCase());
            if (nameMatches.length === 1) {
                existingVisitor = nameMatches[0];
                console.log('Visitor found by name:', existingVisitor);
            }
        }

        // Check if animal exists by chipId
        let existingAnimal = null;
        if (dyreIdData.ChipId) {
            const animalsResponse = await animalStore.load({
                query: { search: dyreIdData.ChipId },
                skipUpdate: true,
            });
            if (animalsResponse && animalsResponse.length > 0) {
                existingAnimal = animalsResponse[0];
                console.log('Animal found:', existingAnimal);
            }
        }

        // Check if selected visitor matches DyreID owner
        let ownerMismatch = false;
        if (selectedVisitor && existingVisitor) {
            // Compare by ID
            ownerMismatch = selectedVisitor.id !== existingVisitor.id;
        } else if (selectedVisitor && !existingVisitor && phoneNumber) {
            // Selected visitor exists but DyreID owner not found in our system
            // Compare by phone number (normalize both for comparison)
            let selectedPhone = selectedVisitor.cellphone || '';
            selectedPhone = selectedPhone.trim().replace(/\s/g, '');
            selectedPhone = selectedPhone.replace(/^(\+)?(.*)$/, (match, plus, rest) => {
                return (plus || '') + rest.replace(/\D/g, '');
            });

            ownerMismatch = selectedPhone !== phoneNumber;
        }

        // Store the status for display
        this.setState({
            dyreIdInfo: {
                ...dyreIdData,
                visitorExists: !!existingVisitor,
                animalExists: !!existingAnimal,
                existingVisitor,
                existingAnimal,
                selectedVisitor,
                ownerMismatch,
                cleanedPhone: phoneNumber, // Store cleaned phone for display
                ownerFirstname,
                ownerLastname,
                ownerName,
            },
            dyreIdError: null,
        });
    }

    importFromDyreId = async () => {
        const dialogAction = beginDialogAction(this, 'importFromDyreId', () => [
            this.state?.dyreIdInfo?.existingVisitor?.id,
            this.props.stores.saleStore.newSale?.id,
            this.state.dyreIdInfo?.ChipId,
        ]);
        if (!dialogAction) return false;
        try {
            const { dyreIdInfo } = this.state;
            const { visitorStore, animalStore, saleStore } = this.props.stores;
            const { callback = () => {} } = this.props;

            if (!dyreIdInfo) {
                return;
            }

            // Load species and breeds to find IDs
            await animalStore.loadSpecies();

            let visitor = dyreIdInfo.existingVisitor;
            let animal = dyreIdInfo.existingAnimal;

            // Ask for confirmation
            const needToCreateVisitor = !dyreIdInfo.visitorExists;
            const needToCreateAnimal = !dyreIdInfo.animalExists;

            const {
                dyreIdImportQuestion, dyreIdCreateVisitor, dyreIdUseVisitor,
                dyreIdCreateAnimal, dyreIdUseAnimal, dyreIdMissingOwnerName,
                dyreIdVisitorFailed, dyreIdAnimalFailed,
            } = this.props;

            // DyreID may know the pet but not the owner. Rather than create a nameless owner,
            // say so and let the clinic register the owner the way they want it.
            if (needToCreateVisitor && !dyreIdInfo.ownerName) {
                await appAlert(dyreIdMissingOwnerName);
                return;
            }

            // Every part is optional in DyreID's answer, so only print what we actually have.
            const newAnimalName = dyreIdInfo.Pedigree || dyreIdInfo.PetName || '';
            const ownerLine = [dyreIdInfo.ownerName, dyreIdInfo.cleanedPhone ? `(${dyreIdInfo.cleanedPhone})` : '']
                .filter(part => part).join(' ');
            const animalLine = [newAnimalName, dyreIdInfo.ChipId ? `(${dyreIdInfo.ChipId})` : '']
                .filter(part => part).join(' ');

            let confirmMessage = `${dyreIdImportQuestion}\n\n`;
            if (needToCreateVisitor) {
                confirmMessage += `- ${dyreIdCreateVisitor}: ${ownerLine}\n`;
            } else {
                confirmMessage += `- ${dyreIdUseVisitor}: ${visitorName(visitor)}\n`;
            }
            if (needToCreateAnimal) {
                confirmMessage += `- ${dyreIdCreateAnimal}: ${animalLine}\n`;
            } else {
                confirmMessage += `- ${dyreIdUseAnimal}: ${animal.name}\n`;
            }

            if (!((await appConfirm(confirmMessage)) && dialogAction.isCurrent())) {
                return;
            }

            // Create visitor if needed
            if (needToCreateVisitor) {
                const newVisitor = {
                    firstname: dyreIdInfo.ownerFirstname,
                    lastname: dyreIdInfo.ownerLastname,
                    cellphone: dyreIdInfo.cleanedPhone,
                    address: dyreIdInfo.AddressLine1,
                    postalcode: dyreIdInfo.PostalCode,
                    place: dyreIdInfo.City,
                };

                const visitorResponse = await visitorStore.insert(newVisitor);
                if (visitorResponse?.status === 201) {
                    visitor = visitorResponse.data.visitor;
                    console.log('Visitor created:', visitor);
                } else {
                    (await appAlert(dyreIdVisitorFailed));
                    return;
                }
            }

            // Create animal if needed
            if (needToCreateAnimal && visitor) {
                const birthDate = dyreIdInfo.BirthYear && dyreIdInfo.BirthMonth && dyreIdInfo.BirthDate
                    ? new Date(dyreIdInfo.BirthYear, parseInt(dyreIdInfo.BirthMonth, 10) - 1, dyreIdInfo.BirthDate)
                    : null;

                // Find species ID by name
                const species = animalStore.species.find(s =>
                    s.name && s.name.toLowerCase() === dyreIdInfo.Species?.toLowerCase()
                );
                const speciesId = species?.id || species?.speciesId;

                // Load breeds for this species and find breed ID
                let breedId = null;
                if (speciesId) {
                    await animalStore.loadBreeds(speciesId);
                    const breed = animalStore.breeds.find(b =>
                        b.name && b.name.toLowerCase() === dyreIdInfo.Breed?.toLowerCase()
                    );
                    breedId = breed?.id || breed?.breedId;
                }

                const newAnimal = {
                    name: dyreIdInfo.Pedigree,
                    pedigreeName: dyreIdInfo.PetName,
                    chipId: dyreIdInfo.ChipId,
                    nkkId: dyreIdInfo.NKKId,
                    speciesId: speciesId,
                    breedId: breedId,
                    gender: this.mapDyreIdGender(dyreIdInfo.PetSex),
                    birth: birthDate,
                    owner: visitor.id,
                    isDececed: dyreIdInfo.IsDead === 'true',
                    isLost: dyreIdInfo.IsLost === 'true',
                };

                console.log('Creating animal with:', newAnimal);

                const animalResponse = await animalStore.insert(newAnimal);
                if (animalResponse?.status === 201) {
                    animal = animalResponse.data.animal;
                    console.log('Animal created:', animal);
                } else {
                    (await appAlert(dyreIdAnimalFailed));
                    return;
                }
            }

            // Add animal to sale
            if (animal) {
                const { newSale } = saleStore;
                const { animals = [] } = newSale;

                const selectedItem = {
                    ...animal,
                    createdDate: new Date(),
                    md5: md5(JSON.stringify({ ...animal, createdDate: new Date() })).toString(),
                };

                saleStore.updateObjectKeyValue('newSale', 'animals', [...animals, selectedItem]);
                if (animals.length === 0) {
                    saleStore.setSelectedTargetAnimal(selectedItem.id);
                }
                await callback();

                // Clear search and DyreID info
                this.clearSearch();
            }
        } finally {
            dialogAction.finish();
        }
    }

    cancelDyreIdImport = () => {
        this.setState({
            dyreIdInfo: null,
            dyreIdError: null,
        });
    }

    mapDyreIdGender = (dyreIdGender) => {
        if (!dyreIdGender) return dyreIdGender;
        const genderLower = dyreIdGender.toLowerCase().trim();
        if (genderLower.includes('kastrert') && genderLower.includes('hann')) {
            return 'Kastrert hann';
        }
        if (genderLower.includes('hann')) return 'Hann';
        if (genderLower.includes('hunn')) return 'Hunn';
        return dyreIdGender;
    }

    checkPreSearch = () => {
        const { search } = this.state;
        const { preSearch } = this.props;
        if (preSearch) {
            this.doSearch(search || ' ');
        }
    }

    chooseLine = (e, idx) => {
        const { search } = this.state;
        const { saleStore } = this.props.stores;
        const { searchResultList, append, callback = () => {}, holdingStore, skipSaleUpdate } = this.props;
        const { visitorResults, animalResults, productResults, newSale } = saleStore;
        const { searchResults } = holdingStore;

        this.saveSearchLocalStorage(search);

        let finalSearchResults = [];
        let newSaleObject = '';
        let newSaleArray = [];
        let selectedItem;
        if (searchResultList === 'visitorResults') {
            finalSearchResults = visitorResults || [];
            newSaleObject = 'visitors';
            newSaleArray = newSale.visitors || [];
            selectedItem = {
                ...finalSearchResults[idx],
                createdDate: new Date(),
                md5: md5(JSON.stringify({ ...finalSearchResults[idx], createdDate: new Date() })).toString(),
            };
        } else if (searchResultList === 'animalResults') {
            finalSearchResults = animalResults || [];
            newSaleObject = 'animals';
            newSaleArray = newSale.animals || [];
            selectedItem = {
                ...finalSearchResults[idx],
                createdDate: new Date(),
                md5: md5(JSON.stringify({ ...finalSearchResults[idx], createdDate: new Date() })).toString(),
            };
        } else if (searchResultList === 'productResults') {
            finalSearchResults = productResults || [];
            newSaleObject = 'products';
            newSaleArray = newSale.products || [];
            selectedItem = {
                ...finalSearchResults[idx],
                qty: 1,
                createdDate: new Date(),
                md5: md5(JSON.stringify({ ...finalSearchResults[idx], createdDate: new Date() })).toString(),
            };
        } else if (searchResultList === 'searchResults') {
            // Use filtered results when filters are active
            const { filterFields } = this.props;
            finalSearchResults = (filterFields && this.displayResults) ? this.displayResults : (searchResults || []);
            newSaleObject = null;
            newSaleArray = null;
            selectedItem = {
                ...finalSearchResults[idx],
                createdDate: new Date(),
                qty: 1,
                md5: md5(JSON.stringify({ ...finalSearchResults[idx], createdDate: new Date() })).toString(),
            };
        }
        if (!finalSearchResults || !finalSearchResults[idx]) {
            return;
        }
        if (newSaleObject && newSaleArray && !skipSaleUpdate) {
            if (append) {
                saleStore.updateObjectKeyValue('newSale', newSaleObject, [...newSaleArray, selectedItem]);
            } else {
                saleStore.updateObjectKeyValue('newSale', newSaleObject, [selectedItem]);
            }
        }
        callback(selectedItem);
    }

    handleKeyDown = e => {
        const { saleStore } = this.props.stores;
        const { searchResultList, holdingStore, filterFields } = this.props;
        const { newSale, visitorResults, animalResults, productResults } = saleStore;
        const { searchResults } = holdingStore;
        let finalSearchResults = [];
        if (searchResultList === 'visitorResults') {
            finalSearchResults = visitorResults || [];
        } else if (searchResultList === 'animalResults') {
            finalSearchResults = animalResults || [];
        } else if (searchResultList === 'productResults') {
            finalSearchResults = productResults || [];
        } else if (searchResultList === 'searchResults') {
            finalSearchResults = searchResults || [];
        }

        // Use filtered results for navigation when filters are active
        if (filterFields && this.displayResults) {
            finalSearchResults = this.displayResults;
        }

        this.setFocus();

        const { selectedIndex } = this.state;
        if (e.keyCode === 38) {
            // Arrow up key pressed
            e.preventDefault();
            e.stopPropagation();
            // console.log('Arrow up key pressed');
            let idx = selectedIndex - 1;
            if (idx < 0) {
                idx = finalSearchResults.length - 1;
            }
            this.setState({
                selectedIndex: idx,
            });
            if (this.lines[idx]) {
                this.lines[idx].scrollIntoView({ behavior: 'smooth', block: 'center', container: this.linesContainer });
            }
        } else if (e.keyCode === 40) {
            // Arrow down key pressed
            e.preventDefault();
            e.stopPropagation();
            // console.log('Arrow down key pressed');
            let idx = selectedIndex + 1;
            if (idx >= finalSearchResults.length) {
                idx = 0;
            }
            this.setState({
                selectedIndex: idx,
            });
            if (this.lines[idx]) {
                this.lines[idx].scrollIntoView({ behavior: 'smooth', block: 'center', container: this.linesContainer });
            }
        } else if (e.keyCode === 13) {
            // Enter key pressed
            e.preventDefault();
            e.stopPropagation();
            // console.log('Enter key pressed');
            this.chooseLine(e, selectedIndex || 0);
            this.clearSearch({ keepFocus: true });
        } else if (e.keyCode === 27) {
            // Esc key pressed
            e.preventDefault();
            e.stopPropagation();
            // console.log('Esc key pressed');
            this.clearSearch();
            this.searchInputField?.blur();
        }
    }

    clearSearch = ({ keepFocus = false } = {}) => {
        const { searchResultList, holdingStore } = this.props;
        const { saleStore } = this.props.stores;
        // Rest search and search results
        this.setState({
            selectedIndex: 0,
            search: '',
            dyreIdInfo: null,
            dyreIdError: null,
            activeFilters: {},
        });
        if (!keepFocus) {
            this.unsetFocus();
        }
        if (searchResultList === 'visitorResults') {
            saleStore.updateKeyValue(searchResultList, []);
        } else if (searchResultList === 'animalResults') {
            saleStore.updateKeyValue(searchResultList, []);
        } else if (searchResultList === 'productResults') {
            saleStore.updateKeyValue(searchResultList, []);
        } else if (searchResultList === 'searchResults') {
            holdingStore.updateKeyValue(searchResultList, []);
        }
        if (keepFocus) {
            // Refocus after state updates to allow immediate next search
            setTimeout(() => {
                this.searchInputField?.focus();
            }, 50);
        }
    }

    clickLine = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { idx } = e.target.closest('.resultLine').dataset;
        this.chooseLine(e, idx);
        this.clearSearch({ keepFocus: true });
    }

    focusInput = () => {
        this.searchInputField?.focus();
    }

    setFocus = e => {
        this.setState({ focused: true });
        this.checkPreSearch();
        if (this.searchInputField) {
            this.searchInputField.focus();
        }
    }

    unsetFocus = e => {
        this.setState({ focused: false });
    }

    handleGlobalShortcut = (e) => {
        const { focusKey } = this.props;
        if (focusKey && e.key === focusKey && !['INPUT', 'TEXTAREA'].includes(e.target.tagName)) {
            e.preventDefault();
            this.searchInputField?.focus();
        }
    }

    componentDidMount() {
        // Register this instance
        searchComponentInstances.add(this);

        // Add the event listener if this is the first instance
        if (searchComponentInstances.size === 1) {
            document.addEventListener('mousedown', this.handleGlobalClick);
        }

        // Add keyboard shortcut listener if focusKey is provided
        const { focusKey } = this.props;
        if (focusKey) {
            document.addEventListener('keydown', this.handleGlobalShortcut);
        }

        this.checkPreSearch();

        const { focusOnMount = false, setFocusCallback } = this.props;
        if (focusOnMount) {
            // Delay focus to wait for drawer animation (300ms CSS transition)
            setTimeout(() => {
                this.searchInputField?.focus();
            }, 350);
        }

        // Pass focus function to parent via callback (for refocusing after operations)
        if (setFocusCallback) {
            setFocusCallback(() => this.searchInputField?.focus());
        }
    }

    componentWillUnmount() {
        // Unregister this instance
        searchComponentInstances.delete(this);

        // Remove the event listener if no instances are left
        if (searchComponentInstances.size === 0) {
            document.removeEventListener('mousedown', this.handleGlobalClick);
        }

        // Remove keyboard shortcut listener if focusKey was provided
        const { focusKey } = this.props;
        if (focusKey) {
            document.removeEventListener('keydown', this.handleGlobalShortcut);
        }
    }

    // Global click handler shared by all instances
    handleGlobalClick = (event) => {
        // While an app dialog is open the rest of the page is inert, so the mousedown belongs to the
        // dialog. The dialog is a portal on document.body, outside every search container; treating
        // its OK button as a click outside wiped dyreIdInfo under the open confirm and made
        // importFromDyreId abort silently (TICKET-01634).
        if (dialogStore.current) {
            return;
        }
        // Iterate over all instances
        searchComponentInstances.forEach((instance) => {
            instance.handleClickOutside(event);
        });
    }

    handleClickOutside = (event) => {
        // Check if the click is outside the search field and suggestions list
        if (this.searchFieldRef && !this.searchFieldRef.contains(event.target)) {
            this.clearSearch();
        }
    }

    scanBarcode = () => {
        const { drawerLevel = 1, barcodeScannerCallback = () => {} } = this.props;
		const { appState, saleStore, animalStore } = this.props.stores;
		const { drawerHeightMedium } = appState;

		appState.openDrawer('scanBarcode', {
			height: drawerHeightMedium,

            callback: data => {
                barcodeScannerCallback(data);
                // console.log('scanBarcode callback', data);
                appState.toggleDrawer(false, drawerLevel + 1);
            },
		}, drawerLevel + 1);
	}

    render() {
        const {
            searchResultList,
            searchResultsLine = () => {},
            searchResultsFields = () => {},
            rightButton,
            rightButtonClick = () => {},
            disabled = false,
            holdingStore,
            rightButtonClassNames = 'btn-outline-secondary',
            keyName = '',
            prependInfo,
            showBarcodeScanner = false,
            focusKey,
            filterFields,
        } = this.props;

        const { saleStore, userStore } = this.props.stores;
        const {
            search,
            selectedIndex,
            searching,
            focused,
            dyreIdInfo,
            dyreIdError,
            activeFilters,
        } = this.state;
        const {
            title,
        } = this.props;

        // Build placeholder text
        let placeholder = title;
        if (focusKey && this.props.searchPlaceholderWithKey) {
            placeholder = this.props.searchPlaceholderWithKey
                .replace('{title}', title)
                .replace('{key}', focusKey);
        }

        const { newSale, visitorResults, animalResults, productResults } = saleStore;
        const { searchResults } = holdingStore;
        let finalSearchResults = [];
        if (searchResultList === 'visitorResults') {
            finalSearchResults = visitorResults || [];
        } else if (searchResultList === 'animalResults') {
            finalSearchResults = animalResults || [];
        } else if (searchResultList === 'productResults') {
            finalSearchResults = productResults || [];
        } else if (searchResultList === 'searchResults') {
            finalSearchResults = searchResults || [];
        }

        const { user = {} } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        const searchHistory = util.get(`searchHistory-${searchResultList}-${keyName}`) || [];

        // Client-side filtering when filterFields prop is provided
        // filterFields is an array of { field, label, formatValue } objects
        let displayResults = finalSearchResults;
        let allFilterChips = [];
        if (filterFields && filterFields.length > 0 && finalSearchResults.length > 0) {
            // Apply all active filters
            filterFields.forEach(({ field }) => {
                const filterValue = activeFilters[field];
                if (filterValue) {
                    displayResults = displayResults.filter(r => String(r[field]) === filterValue);
                }
            });
            // Compute chips for each filter field based on the currently filtered results
            // (but don't filter by the field we're computing chips for)
            filterFields.forEach(({ field, label, formatValue }) => {
                // Start from results filtered by OTHER fields only
                let baseResults = finalSearchResults;
                filterFields.forEach(({ field: otherField }) => {
                    const otherValue = activeFilters[otherField];
                    if (otherValue && otherField !== field) {
                        baseResults = baseResults.filter(r => String(r[otherField]) === otherValue);
                    }
                });
                const filterCounts = {};
                baseResults.forEach(r => {
                    const val = r[field];
                    if (val !== undefined && val !== null && val !== '') {
                        const key = String(val);
                        filterCounts[key] = (filterCounts[key] || 0) + 1;
                    }
                });
                const entries = Object.entries(filterCounts).sort((a, b) => b[1] - a[1]);
                if (entries.length >= 2) {
                    allFilterChips.push({ field, label, formatValue, entries, total: baseResults.length });
                }
            });
        }
        this.displayResults = displayResults;

        return (<>
            <div
                class='d-flex flex-column justify-content-center px-3 mb-1 position-relative'
                ref={c => this.searchFieldRef = c}
            >
                <div class='input-group position-relative mb-0'>
                    <div class='input-group-text rounded-pill-left'>
                        <i class='fa-regular fa-magnifying-glass'></i>
                    </div>
                    {prependInfo && <div class='input-group-text '>
                        <small class='font-weight-light' style='font-size: 0.8em; line-height: 1.0;'>
                            {prependInfo}
                        </small>
                    </div>}

                    <input
                        disabled={disabled}
                        class={`form-control form-control-sm ${!search && !rightButton && !showBarcodeScanner ? 'rounded-pill-right' : ''}`}
                        type='text'
                        value={search}
                        placeholder={placeholder}
                        onInput={this.searchInput}
                        onKeyDown={this.handleKeyDown}
                        onFocus={this.setFocus}
                        ref={c => this.searchInputField = c}
                        // onBlur={this.unsetFocus}
                    />
                    {showBarcodeScanner && <button class={`btn btn-sm btn-outline-secondary ${!search && !rightButton ? 'rounded-pill-right' : 'rounded-none'}`} type='button' onClick={this.scanBarcode}>
                        <i class='fa-solid fa-barcode'></i>
                    </button>}
                    {rightButton ? <>
                        {search && <>
                            <button class='btn btn-sm btn-danger rounded-none' type='button' onClick={() => this.clearSearch({ keepFocus: true })}>
                                <i class='fa-solid fa-circle-xmark'></i>
                            </button>
                        </>}
                        <button
                            // disabled={disabled}
                            class={`btn btn-sm ${rightButtonClassNames} rounded-pill-right`}
                            type='button'
                            onClick={rightButtonClick}
                        >
                            <i class='fa-solid fa-plus' /> <span class='d-none d-md-inline-block'>{rightButton}</span>
                        </button>
                    </> : <>
                        {search && <>
                            <button class='btn btn-sm btn-danger rounded-pill-right' type='button' onClick={() => this.clearSearch({ keepFocus: true })}>
                                <i class='fa-solid fa-circle-xmark'></i>
                            </button>
                        </>}
                    </>}
                    <div class='position-absolute' style='right: 55px; top: 5px; font-size: 1.2em; z-index: 1000;'>
                        {searching && <i class='fa-solid fa-spinner fa-spin text-secondary' />}
                    </div>
                </div>

                <div class='d-flex flex-row flex-wrap align-items-center mt-1 px-1' style='min-height: 18px;'>
                    {focused && searchHistory.length > 0 && <>
                        <small class='text-body-secondary me-1' style='font-size: 0.7em;'><i class='fa-solid fa-clock-rotate-left' /></small>
                        {searchHistory.map((item, idx) => {
                            return (<>
                                <span
                                    class='badge bg-body-secondary text-body-secondary rounded-pill mx-1'
                                    style='cursor: pointer; font-size: 0.65em; font-weight: normal; line-height: 1; padding: 3px 6px; max-height: 18px;'
                                    onClick={() => this.searchFromHistory(item)}
                                >
                                    {item}
                                </span>
                            </>);
                        })}
                    </>}
                </div>

                {allFilterChips.length > 0 && <>
                    {allFilterChips.map(({ field, label, formatValue, entries, total }) => (
                        <div class='d-flex flex-row flex-wrap align-items-center gap-1 px-1 mt-1'>
                            {label && <small class='text-body-secondary me-1' style='font-size: 0.7em;'>{label}:</small>}
                            <span
                                class={`badge rounded-pill ${!activeFilters[field] ? 'bg-primary' : 'bg-body-secondary text-body-secondary'}`}
                                style='cursor: pointer; font-size: 0.75em; font-weight: normal;'
                                onClick={() => {
                                    const newFilters = { ...activeFilters };
                                    delete newFilters[field];
                                    this.setState({ activeFilters: newFilters, selectedIndex: 0 });
                                }}
                            >
                                Alle ({total})
                            </span>
                            {entries.map(([val, count]) => (
                                <span
                                    class={`badge rounded-pill ${activeFilters[field] === val ? 'bg-primary' : 'bg-body-secondary text-body-secondary'}`}
                                    style='cursor: pointer; font-size: 0.75em; font-weight: normal;'
                                    onClick={() => this.setState({
                                        activeFilters: { ...activeFilters, [field]: val },
                                        selectedIndex: 0,
                                    })}
                                >
                                    {formatValue ? formatValue(val) : val} ({count})
                                </span>
                            ))}
                        </div>
                    ))}
                </>}

                {searchResultsFields && displayResults.length ? <>
                    <div class={`d-flex flex-row w-100 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} position-absolute border rounded-lg`} style='z-index: 10000; max-height: 60vh; top: 100%;'>
                        <div class='d-flex flex-column' style='width: 50%; border-right: 1px solid rgba(0,0,0,0.1);'>
                            <div class={`px-2 overflow-auto`} ref={r => this.linesContainer = r} style='max-height: 60vh;'>
                                <table class='table table-sm table-striped table-hover mb-0'>
                                    <tbody>
                                        {displayResults.map((res, idx) => {
                                            return (<>
                                                <tr>
                                                    <td
                                                        class='resultLine px-0 py-0'
                                                        onClick={this.clickLine}
                                                        data-idx={idx}
                                                        ref={r => this.lines[idx] = r}
                                                    >
                                                        {searchResultsLine(res, idx, selectedIndex)}
                                                    </td>
                                                </tr>
                                            </>);
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div class='d-flex flex-column overflow-auto px-3 py-2' style='width: 50%; max-height: 60vh;'>
                            {displayResults[selectedIndex] && searchResultsFields(displayResults[selectedIndex])}
                        </div>
                    </div>
                </> : <>
                    {(displayResults && displayResults.length > 0) && <>
                        <div
                            class='position-absolute w-100 overflow-auto px-5'
                            style={`
                                max-height: 60vh;
                                top: 100%;
                                left: 0px;
                                z-index: 10050;
                            `}
                        >
                            <div class={`px-2 bg-light border box-container h-100 overflow-auto`} ref={r => this.linesContainer = r}>
                                {displayResults.map((res, idx) => {
                                    return (<>
                                        <div class='resultLine' onClick={this.clickLine} data-idx={idx} ref={r => this.lines[idx] = r}>
                                            {searchResultsLine(res, idx, selectedIndex)}
                                        </div>
                                    </>);
                                })}
                            </div>
                        </div>
                    </>}
                </>}

                {/* DyreID lookup results */}
                {dyreIdError && <>
                    <div class='alert alert-warning mt-2 mb-0 py-2 px-3'>
                        <i class='fa-solid fa-exclamation-triangle me-2' />
                        {dyreIdError}
                    </div>
                </>}

                {dyreIdInfo && <>
                    <div class='alert alert-info rounded-lg mt-2 mb-0 py-2 px-3'>
                        <div class='d-flex flex-column'>
                            <div class='mb-2 d-flex flex-row justify-content-between align-items-center'>
                                <div>
                                    <i class='fa-solid fa-info-circle me-2' />
                                    <strong><Text id='dyreid.animal-found-in-dyreid'>Animal found in DyreID:</Text></strong>
                                </div>
                                <div>
                                    <button class='btn btn-sm btn-secondary rounded-pill me-2' onClick={this.cancelDyreIdImport}>
                                        <i class='fa-solid fa-times me-1' /> <Text id='dyreid.cancel'>Cancel</Text>
                                    </button>
                                    <button class='btn btn-sm btn-primary rounded-pill' onClick={this.importFromDyreId}>
                                        <i class='fa-solid fa-download me-1' /> <Text id='dyreid.import'>Import</Text>
                                    </button>
                                </div>
                            </div>

                            {dyreIdInfo.ownerMismatch && <>
                                <div class='alert alert-danger mb-2 py-2 px-3'>
                                    <div class='fw-bold'>
                                        <i class='fa-solid fa-exclamation-triangle me-2' />
                                        <Text id='dyreid.warning-owner-mismatch'>WARNING: Owner Mismatch!</Text>
                                    </div>
                                    <div class='mt-2'>
                                        <div><strong><Text id='dyreid.selected-visitor'>Selected visitor:</Text></strong> {dyreIdInfo.selectedVisitor?.firstname} {dyreIdInfo.selectedVisitor?.lastname} ({dyreIdInfo.selectedVisitor?.cellphone})</div>
                                        <div class='mt-1'><strong><Text id='dyreid.dyreid-owner'>DyreID owner:</Text></strong> {dyreIdInfo.ownerName} {dyreIdInfo.cleanedPhone ? `(${dyreIdInfo.cleanedPhone})` : ''}</div>
                                    </div>
                                    <div class='mt-2 small'>
                                        <i class='fa-solid fa-lightbulb me-1' />
                                        <Text id='dyreid.owner-mismatch-warning'>The animal in DyreID belongs to a different owner than the currently selected visitor. Make sure you select the correct visitor before importing.</Text>
                                    </div>
                                </div>
                            </>}

                            <div class='mb-2'>
                                <div class='fw-bold'><Text id='dyreid.visitor-status'>Visitor Status:</Text></div>
                                {dyreIdInfo.visitorExists ? <>
                                    <div class='text-success'>
                                        <i class='fa-solid fa-check-circle me-1' />
                                        <Text id='dyreid.exists'>Exists:</Text> {dyreIdInfo.existingVisitor?.firstname} {dyreIdInfo.existingVisitor?.lastname} ({dyreIdInfo.existingVisitor?.cellphone})
                                    </div>
                                    {dyreIdInfo.existingVisitor?.address && <div class='ms-3 small text-muted'>{dyreIdInfo.existingVisitor.address}, {dyreIdInfo.existingVisitor.postalcode} {dyreIdInfo.existingVisitor.place}</div>}
                                </> : <>
                                    <div class='text-dark fw-bold'>
                                        <i class='fa-solid fa-plus-circle me-1 text-warning' />
                                        <Text id='dyreid.will-be-created'>Will be created:</Text> {dyreIdInfo.ownerName}
                                    </div>
                                    <div class='ms-3 small text-dark'>
                                        {dyreIdInfo.cleanedPhone}
                                        {dyreIdInfo.AddressLine1 && <div>{dyreIdInfo.AddressLine1}, {dyreIdInfo.PostalCode} {dyreIdInfo.City}</div>}
                                    </div>
                                </>}
                            </div>

                            <div class='mb-2'>
                                <div class='fw-bold'><Text id='dyreid.animal-status'>Animal Status:</Text></div>
                                {dyreIdInfo.animalExists ? <>
                                    <div class='text-success'>
                                        <i class='fa-solid fa-check-circle me-1' />
                                        <Text id='dyreid.exists'>Exists:</Text> {dyreIdInfo.existingAnimal?.name} (<Text id='dyreid.chip'>Chip:</Text> {dyreIdInfo.existingAnimal?.chipId})
                                    </div>
                                </> : <>
                                    <div class='text-dark fw-bold'>
                                        <i class='fa-solid fa-plus-circle me-1 text-warning' />
                                        <Text id='dyreid.will-be-created'>Will be created:</Text> {dyreIdInfo.Pedigree} (<Text id='dyreid.chip'>Chip:</Text> {dyreIdInfo.ChipId})
                                    </div>
                                </>}
                            </div>

                            <div class='ms-2 small'>
                                <div><strong><Text id='dyreid.species'>Species:</Text></strong> {dyreIdInfo.Species}</div>
                                <div><strong><Text id='dyreid.breed'>Breed:</Text></strong> {dyreIdInfo.Breed}</div>
                                {dyreIdInfo.PetSex && <div><strong><Text id='dyreid.gender'>Gender:</Text></strong> {dyreIdInfo.PetSex}</div>}
                                {(dyreIdInfo.BirthYear && dyreIdInfo.BirthMonth && dyreIdInfo.BirthDate) && <div><strong><Text id='dyreid.birth'>Birth:</Text></strong> {dyreIdInfo.BirthDate}.{dyreIdInfo.BirthMonth}.{dyreIdInfo.BirthYear}</div>}
                                {dyreIdInfo.PetName && <div><strong><Text id='dyreid.pedigree'>Pedigree:</Text></strong> {dyreIdInfo.PetName}</div>}
                                {dyreIdInfo.NKKId && <div><strong><Text id='dyreid.nkk-id'>NKK ID:</Text></strong> {dyreIdInfo.NKKId}</div>}
                            </div>
                        </div>
                    </div>
                </>}

            </div>
        </>);
    }
}

export default DyrejournalSaleSearch;
